import React, { useRef, useState, useEffect, useCallback } from 'react';
import './ConsolePane.css';

export default function ConsolePane({ height = 140, onHeightChange, messages = [], onClear, onFix, hasError = false, isLoading = false }) {
  const [currentHeight, setCurrentHeight] = useState(height);
  const draggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);
  const handleRef = useRef(null);
  const paneRef = useRef(null);
  const iframeStatesRef = useRef([]);
  const overlayRef = useRef(null);

  const [autoEnabled, setAutoEnabled] = useState(false);
  const [maxTries, setMaxTries] = useState(5);
  const [triesLeft, setTriesLeft] = useState(0);
  const autoTimerRef = useRef(null);
  const noErrorTimerRef = useRef(null);
  const awaitingResponseRef = useRef(false);
  const lastLoadingRef = useRef(false);
  const prevHasErrorRef = useRef(!!hasError);
  const autoStartedRef = useRef(false);

  const disableIframes = () => {
    try {
      // Only disable the preview iframe to avoid affecting other embedded frames
      iframeStatesRef.current = [];
      const f = document.getElementById('html-output-iframe');
      if (f) {
        iframeStatesRef.current.push([f, f.style.pointerEvents || '']);
        f.style.pointerEvents = 'none';
      }
    } catch (err) {
      // ignore
    }
  };

  const restoreIframes = () => {
    try {
      (iframeStatesRef.current || []).forEach(([f, prev]) => {
        try { f.style.pointerEvents = prev || ''; } catch (e) {}
      });
      iframeStatesRef.current = [];
    } catch (err) {
      // ignore
    }
  };

  const createOverlay = () => {
    try {
      const o = document.createElement('div');
      o.style.position = 'fixed';
      o.style.left = '0';
      o.style.top = '0';
      o.style.right = '0';
      o.style.bottom = '0';
      o.style.zIndex = '2147483647';
      o.style.background = 'transparent';
      o.style.cursor = 'ns-resize';
      o.style.touchAction = 'none';
      document.body.appendChild(o);
      overlayRef.current = o;
    } catch (err) {
      overlayRef.current = null;
    }
  };

  const removeOverlay = () => {
    try {
      if (overlayRef.current) {
        overlayRef.current.remove();
        overlayRef.current = null;
      }
    } catch (err) {
      // ignore
    }
  };

  const buildFixText = useCallback(() => {
    try {
      const last = messages && messages.length ? messages[messages.length - 1] : null;
      const msg = last && last.message ? String(last.message) : '';
      const line = (last && last.line != null) ? last.line : null;
      const col = (last && last.col != null) ? last.col : null;
      const loc = (line != null)
        ? `\nLine: ${line}${col != null ? `, Col: ${col}` : ''}`
        : '';
      return `${msg}${loc}`.trim();
    } catch (_) {
      return '';
    }
  }, [messages]);

  const endAutoMode = useCallback((resetTriesTo) => {
    try {
      setAutoEnabled(false);
      const n = Number(resetTriesTo);
      setTriesLeft(Number.isFinite(n) ? n : 0);
      autoStartedRef.current = false;
      awaitingResponseRef.current = false;
      if (autoTimerRef.current) {
        clearTimeout(autoTimerRef.current);
        autoTimerRef.current = null;
      }
      if (noErrorTimerRef.current) {
        clearTimeout(noErrorTimerRef.current);
        noErrorTimerRef.current = null;
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    setCurrentHeight(height);
  }, [height]);

  useEffect(() => {
    return () => {
      try {
        if (autoTimerRef.current) {
          clearTimeout(autoTimerRef.current);
          autoTimerRef.current = null;
        }
        if (noErrorTimerRef.current) {
          clearTimeout(noErrorTimerRef.current);
          noErrorTimerRef.current = null;
        }
      } catch (_) {}
    };
  }, []);

  useEffect(() => {
    // When the API reports insufficient credit, stop auto-fix immediately.
    const onInsufficientCredit = () => {
      try { endAutoMode(Number(maxTries) || 0); } catch (_) {}
    };
    try { window.addEventListener('appstar-insufficient-credit', onInsufficientCredit); } catch (_) {}
    return () => {
      try { window.removeEventListener('appstar-insufficient-credit', onInsufficientCredit); } catch (_) {}
    };
  }, [endAutoMode, maxTries]);

  useEffect(() => {
    // If errors disappear (fixed/cleared), stop auto mode and reset tries.
    const prev = !!prevHasErrorRef.current;
    const now = !!hasError;
    prevHasErrorRef.current = now;
    if (prev && !now) {
      try { endAutoMode(Number(maxTries) || 0); } catch (_) {}
    }
  }, [hasError, endAutoMode, maxTries]);

  useEffect(() => {
    // Track when an API response completes after a Fix Error send
    try {
      const wasLoading = !!lastLoadingRef.current;
      const nowLoading = !!isLoading;
      lastLoadingRef.current = nowLoading;
      if (wasLoading && !nowLoading) {
        awaitingResponseRef.current = false;

        // If we're in Auto mode and the response just finished, end Auto mode only
        // if the console stays error-free for a short grace period.
        if (autoEnabled && (triesLeft || 0) > 0) {
          try {
            if (noErrorTimerRef.current) {
              clearTimeout(noErrorTimerRef.current);
              noErrorTimerRef.current = null;
            }
          } catch (_) {}

          const GRACE_MS = 900;
          noErrorTimerRef.current = setTimeout(() => {
            noErrorTimerRef.current = null;
            try {
              if (!autoEnabled) return;
              if ((triesLeft || 0) <= 0) return;
              if (isLoading) return;
              if (awaitingResponseRef.current) return;
              if (hasError) return;
              // No error after grace period => end
              endAutoMode(Number(maxTries) || 0);
            } catch (_) {}
          }, GRACE_MS);
        }
      }
    } catch (_) {}
  }, [isLoading, autoEnabled, triesLeft, hasError, endAutoMode, maxTries]);

  useEffect(() => {
    // If a new error appears, cancel any pending "end auto mode" timer.
    if (!hasError) return;
    try {
      if (noErrorTimerRef.current) {
        clearTimeout(noErrorTimerRef.current);
        noErrorTimerRef.current = null;
      }
    } catch (_) {}
  }, [hasError]);

  useEffect(() => {
    const handleMove = (e) => {
      if (!draggingRef.current) return;
      // prevent scrolling while dragging on touch devices
      if (e.type === 'touchmove') e.preventDefault();
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      // compute new height based on pane bottom so dragging up increases height
      const paneBottom = paneRef.current && paneRef.current._startBottom ? paneRef.current._startBottom : window.innerHeight;
      const newHeight = Math.max(60, Math.min(window.innerHeight * 0.8, paneBottom - clientY));
      setCurrentHeight(newHeight);
      if (onHeightChange) onHeightChange(newHeight);
    };

    const handleUp = () => {
      draggingRef.current = false;
      // restore iframes
      restoreIframes();
      // re-enable text selection
      try { document.body.style.userSelect = ''; } catch (e) {}
      // remove overlay and restore cursor
      try { removeOverlay(); document.body.style.cursor = ''; } catch (e) {}
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchend', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchend', handleUp);
    };
  }, [onHeightChange]);

  const triggerFix = useCallback((source) => {
    // source: 'manual' | 'auto'
    try {
      // Never send another Fix Error while waiting for the previous response
      if (awaitingResponseRef.current || isLoading) return;

      // Auto mode should not send anything until the user presses Fix Error once.
      if (source === 'manual' && autoEnabled) {
        autoStartedRef.current = true;
      }

      const text = buildFixText();
      // If Auto mode is enabled, each Fix Error click consumes one try
      if (autoEnabled) {
        setTriesLeft((prev) => {
          const prevNum = Number(prev) || 0;
          const initial = (prevNum > 0) ? prevNum : (Number(maxTries) || 0);
          const next = Math.max(0, initial - 1);
          if (next === 0) {
            // Auto mode ends once counter reaches 0
            try { endAutoMode(0); } catch (_) {}
          }
          return next;
        });
      }

      awaitingResponseRef.current = true;
      if (onFix) onFix(text);
    } catch (_) {
      try { if (onFix) onFix(''); } catch (_) {}
    }
  }, [autoEnabled, maxTries, onFix, buildFixText, endAutoMode, isLoading]);

  useEffect(() => {
    // Auto mode only runs after the user has manually started it (triesLeft > 0).
    if (!autoEnabled) return;
    if (!autoStartedRef.current) return;
    if (!hasError) return;
    if (!triesLeft || triesLeft <= 0) return;
    if (!messages || messages.length === 0) return;
    if (isLoading) return;
    if (awaitingResponseRef.current) return;

    if (autoTimerRef.current) return;
    autoTimerRef.current = setTimeout(() => {
      autoTimerRef.current = null;
      try {
        // Auto-click Fix Error
        triggerFix('auto');
      } catch (_) {}
    }, 350);

    return () => {
      try {
        if (autoTimerRef.current) {
          clearTimeout(autoTimerRef.current);
          autoTimerRef.current = null;
        }
      } catch (_) {}
    };
  }, [autoEnabled, hasError, triesLeft, messages, triggerFix, isLoading, endAutoMode]);

  const onPointerDown = (e) => {
    // If the pointerdown originated from an interactive control (button/link/input), don't start dragging
    try {
      const target = e.target || e.srcElement;
      if (target && target.closest) {
        if (target.closest('button, a, input, textarea, select, label')) return;
      }
    } catch (err) {}
    e.preventDefault();
    draggingRef.current = true;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    startYRef.current = clientY;
    startHeightRef.current = currentHeight;
    if (paneRef.current) {
      const rect = paneRef.current.getBoundingClientRect();
      paneRef.current._startBottom = rect.bottom;
    }
    // disable pointer events on iframes so the drag continues when pointer moves over them
    disableIframes();
    // prevent text selection while dragging
    try { document.body.style.userSelect = 'none'; } catch (e) {}
    // create global overlay to capture events and ensure cursor is consistent
    try { createOverlay(); document.body.style.cursor = 'ns-resize'; } catch (e) {}
    try { e.target.setPointerCapture && e.target.setPointerCapture(e.pointerId); } catch (err) {}
  };

  return (
  <div ref={paneRef} className="console-pane" style={{ height: currentHeight }}>
      <div className="console-titlebar" onMouseDown={onPointerDown} onTouchStart={onPointerDown} role="separator" aria-orientation="vertical" title="Drag to resize">
        <div className="console-title">Console</div>
        <div className="console-controls" style={{ marginLeft: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* Fix Error button placed in titlebar; only visible when an error exists */}
          {hasError ? (
            <button
              className="console-fix-btn"
              title="Fix Error"
              onClick={() => {
                triggerFix('manual');
              }}
            >Fix Error{autoEnabled && triesLeft > 0 ? <span className="console-tries"> ({triesLeft} left)</span> : null}</button>
          ) : null}

          <label className="console-auto-toggle" title="Auto mode">
            <span className="console-auto-label">Auto mode</span>
            <input
              type="checkbox"
              checked={autoEnabled}
              onChange={(e) => {
                const next = !!(e && e.target && e.target.checked);
                setAutoEnabled(next);
                autoStartedRef.current = false;
                if (next) {
                  // Arm auto mode; do not send until the user clicks Fix Error.
                  setTriesLeft(Number(maxTries) || 0);
                } else {
                  setTriesLeft(0);
                  awaitingResponseRef.current = false;
                  try {
                    if (autoTimerRef.current) {
                      clearTimeout(autoTimerRef.current);
                      autoTimerRef.current = null;
                    }
                  } catch (_) {}
                  try {
                    if (noErrorTimerRef.current) {
                      clearTimeout(noErrorTimerRef.current);
                      noErrorTimerRef.current = null;
                    }
                  } catch (_) {}
                }
              }}
            />
          </label>

          <label className="console-max-tries" title="Max tries">
            <span className="console-auto-label">Max tries</span>
            <select
              value={String(maxTries)}
              onChange={(e) => {
                const v = Number(e && e.target ? e.target.value : 5);
                setMaxTries(Number.isFinite(v) ? v : 5);
              }}
            >
              {[2, 5, 10, 15, 20, 25].map(v => (
                <option key={v} value={String(v)}>{v}</option>
              ))}
            </select>
          </label>
        </div>
        <div ref={handleRef} className="console-drag-handle">
          <div className="drag-bar" />
        </div>
      </div>
      <div className="console-body">
        <div className="console-output">
          <div style={{ padding: 8, color: '#cfcfcf', fontFamily: 'monospace', fontSize: 13 }}>
            {messages.length === 0 ? (
              <div style={{ color: 'rgba(255,255,255,0.45)' }}>Console is empty.</div>
            ) : (
              messages.map((m, idx) => (
                <div key={idx} className="console-entry" style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 600, color: '#ff6b6b' }}>{m.message}</div>
                  {m.line != null && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Line: {m.line}{m.col ? `, Col: ${m.col}` : ''}</div>}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
